import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import eventService from "../../features/eventManagement/service/eventService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/eventManagement/types/eventTypes";

export const fetchEvents = createAsyncThunk(
  "events/fetchAll",
  async (filters, { rejectWithValue }) => {
    try {
      return await eventService.getEvents(filters);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchEventById = createAsyncThunk(
  "events/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      return await eventService.getEventById(id);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createEvent = createAsyncThunk(
  "events/create",
  async ({ data, createdBy }, { rejectWithValue }) => {
    try {
      const event = await eventService.createEvent(data, createdBy);
      return event;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateEvent = createAsyncThunk(
  "events/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await eventService.updateEvent(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteEvent = createAsyncThunk(
  "events/delete",
  async (id, { rejectWithValue }) => {
    try {
      await eventService.deleteEvent(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const assignStaff = createAsyncThunk(
  "events/assignStaff",
  async ({ eventId, staffMember }, { rejectWithValue }) => {
    try {
      return await eventService.assignStaff(eventId, staffMember);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.STAFF_ADD_FAILED);
    }
  },
);

export const assignEquipment = createAsyncThunk(
  "events/assignEquipment",
  async ({ eventId, equipmentItem }, { rejectWithValue }) => {
    try {
      return await eventService.assignEquipment(eventId, equipmentItem);
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.EQUIPMENT_ADD_FAILED,
      );
    }
  },
);

export const addSchedule = createAsyncThunk(
  "events/addSchedule",
  async ({ eventId, scheduleDay }, { rejectWithValue }) => {
    try {
      return await eventService.addSchedule(eventId, scheduleDay);
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.SCHEDULE_ADD_FAILED,
      );
    }
  },
);

export const addExpense = createAsyncThunk(
  "events/addExpense",
  async ({ eventId, expense }, { rejectWithValue }) => {
    try {
      return await eventService.addExpense(eventId, expense);
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.EXPENSE_ADD_FAILED,
      );
    }
  },
);

export const updateWorkflow = createAsyncThunk(
  "events/updateWorkflow",
  async ({ eventId, workflowUpdates }, { rejectWithValue }) => {
    try {
      return await eventService.updateWorkflow(eventId, workflowUpdates);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const updateInvoiceInfo = createAsyncThunk(
  "events/updateInvoice",
  async ({ eventId, invoiceUpdates }, { rejectWithValue }) => {
    try {
      return await eventService.updateInvoice(eventId, invoiceUpdates);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const fetchEventStats = createAsyncThunk(
  "events/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      return await eventService.getEventStats();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  events: [],
  selectedEvent: null,
  stats: null,
  loading: false,
  error: null,
  success: null,
  filters: {
    status: "all",
    eventType: "all",
    search: "",
  },
};

const upsertEventInList = (state, updatedEvent) => {
  const idx = state.events.findIndex((e) => e.id === updatedEvent.id);
  if (idx !== -1) {
    state.events[idx] = updatedEvent;
  }
  if (state.selectedEvent?.id === updatedEvent.id) {
    state.selectedEvent = updatedEvent;
  }
};

const eventSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    clearErrorState: (state) => {
      state.error = null;
    },
    clearSuccessState: (state) => {
      state.success = null;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchEventById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEventById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEvent = action.payload;
      })
      .addCase(fetchEventById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateEvent.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.events = state.events.filter((e) => e.id !== action.payload);
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(assignStaff.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.STAFF_ASSIGNED;
      })
      .addCase(assignStaff.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(assignEquipment.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.EQUIPMENT_ASSIGNED;
      })
      .addCase(assignEquipment.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(addSchedule.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
      })
      .addCase(addSchedule.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(addExpense.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.EXPENSE_ADDED;
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateWorkflow.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
      })

      .addCase(updateInvoiceInfo.fulfilled, (state, action) => {
        upsertEventInList(state, action.payload);
      })

      .addCase(fetchEventStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const {
  clearErrorState,
  clearSuccessState,
  updateFilters,
  clearSelectedEvent,
} = eventSlice.actions;

export default eventSlice.reducer;
